import { useState, useEffect } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import { TRowItem } from "../../components/Table/TableRow";
import DamsTable from "../../components/Table/CollapsibleTable";

import { AssetAssignmentLog, IPagination } from "../../config/types";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { usePagination } from "../../hooks/usePagination";
import { useMangeTableItems } from "../../hooks/useManageTableItems";

import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import {
  assignmentApi,
  GetAssetAssignmentLogsQuery,
} from "../../services/assets/asset-assignment";
import { formatDate } from "../../utils/common";
import { BadgeLabel } from "../../components/Label";
import { AssetAssignmentStatus } from "../../config/constants/common";

const OutBondAssetAssignmentHistoryPage = () => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any[]>(assignmentApi.getAssetAssignments);

  const [querySet] = useState<GetAssetAssignmentLogsQuery>({
    is_deleted: false,
  });
  const populateDataSet = (
    data: AssetAssignmentLog[],
    pagination?: IPagination
  ) => {
    const formatted_data = data.map((record) => ({
      ...record,
      asset_brand: `${record.asset_brand}(${record.asset_model})`,
      user_email: `${record.user_email}  (${
        record.user_first_name + record.user_last_name
      })`,
      created_at: formatDate(record.created_at),
    }));
    setDataset(formatted_data);
    updatePaginationInitState(pagination);
  };
  const fetchData = async () => {
    const result = await assignmentApi.getAssetAssignments(querySet);
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };
  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRestoreRecord = async (data: TRowItem) => {
    actionDialogStore.open({
      title: "Restore Record",
      subtitle: `Restore this deleted assignment record to active status.`,
      onRightBtnPress: () => handleRestoreRequest(data.id),
      rightBtnTxt: "Confirm",
      onLeftBtnPress: () => {},
      type: "ACTION",
    });
  };

  const toggleUnassignAsset = async (data: TRowItem) => {
    actionDialogStore.open({
      title: "Unassign Asset",
      subtitle: `This will unassign the selected asset from the current user. Once unassigned, the asset will be available for reassignment by an administrator.`,
      onRightBtnPress: () => handleUnassignRequest(data.id),
      rightBtnTxt: "Confirm",
      onLeftBtnPress: () => {},
      type: "ACTION",
    });
  };

  async function handleRestoreRequest(id: string) {
    const res = await assignmentApi.restoreAssetAssignment(id ?? "");
    if (res.success) {
      await fetchData();
      resetSelectedRowItems();
      alertDialogStore.open({
        title: res.success
          ? "Restore Asset Assignment"
          : "Restore Assignment Failed",
        subtitle: res.message,
        isSuccess: res.success,
      });
    }
  }

  async function handleUnassignRequest(id: string) {
    const res = await assignmentApi.unassignAsset(id ?? "");
    if (res.success) {
      await fetchData();
      resetSelectedRowItems();
      alertDialogStore.open({
        title: res.success ? "Asset Unassigned" : "Unassign Asset Failed",
        subtitle: res.message,
        isSuccess: res.success,
      });
    }
  }

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };

    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={assignmentApi.deleteAssetAssignment}
          idKey={"id"}
        />
      </Stack>
    );
  };

  const ExtraRowContent = ({ item }: { item: TRowItem }) => {
    const canRestore = querySet.is_deleted === true;

    return (
      <Stack alignItems={"center"} direction={"row"} spacing={1}>
        <BadgeLabel
          onClick={() => toggleUnassignAsset(item)}
          title="Undo"
          tip="Unassign this asset from the current user. The asset will be moved to the unassigned state and can only be reassigned by an administrator."
          colorType="red"
        />

        {canRestore && (
          <BadgeLabel
            onClick={() => toggleRestoreRecord(item)}
            title={"Restore"}
            tip="Restore this deleted asset assignment record to active status."
            colorType="green"
          />
        )}
      </Stack>
    );
  };
  return (
    <MainPageBase
      pageState={"main"}
      title="Outbound Asset Assignment History"
      subtitle="Comprehensive log of all asset assignment activities, including pending, completed, and restored records."
      exportBtnOnClick={() => {
        alert("Exporting...");
      }}
      exportBtnText={"Export"}
    >
      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Stack width={"100%"}>
          <DamsTable
            tableTitle="Asset Assignment Records"
            tableSubtitle="View, filter, and manage historical asset assignment details for improved tracking and accountability."
            numTableRecords="25"
            tableHeaders={columns}
            tableKeys={tkeys}
            data={dataset}
            maxRowCells={6}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            useTableCheckbox
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            selectedRowItems={selectedRowItems}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            headerDropDownMenu={renderHeaderDropDownMenu}
            extraRowComponent={ExtraRowContent}
          />
        </Stack>
      </Box>
    </MainPageBase>
  );
};

const tkeys: (keyof AssetAssignmentLog)[] = [
  "asset_brand",
  "asset_serial_number",
  "user_email",
  "user_category",
  "asset_assignment_status",
  "created_at",
];

const columns = [
  "Asset Brand (Model)",
  "Serial Number",
  "Assigned User - Email (Full Name)",
  "User Category",
  "Assignment Status",
  "Date of Assignment",
];
const hkeys: (keyof AssetAssignmentLog)[] = [
  "remarks",
  "asset_current_location",
  "user_domain",
];

const hcolumns = ["Remarks", "Asset Current Location", "User Domain"];

export default OutBondAssetAssignmentHistoryPage;
